/**
 * @module stb/ui/input
 * @author Igor Zaporozhets <deadbyelpy@gmail.com>
 * @license GNU GENERAL PUBLIC LICENSE Version 3
 */

'use strict';

var Component = require('spa-component'),
    keys = require('stb-keys');


/* eslint no-path-concat: 0 */
/* eslint max-len: 0 */

/**
 * Base input field implementation.
 * Has two types: text and password.
 * Password - replace real text with '*', but real text presents in the own property 'value'.
 *
 * @constructor
 * @extends Component
 *
 * @param {Object} [config={}] init parameters (all inherited from the parent)
 * @param {string} [config.value='text'] input text value
 * @param {string} [config.placeholder='password'] placeholder text value
 * @param {string} [config.type=Input.TYPE_TEXT] input type
 * @param {string} [config.direction='ltr'] symbol direction ('rtl' - right to left, 'ltr' - left to right)
 *
 * @example
 * var Input = require('stb/ui/input'),
 *     input = new Input({
 *         placeholder: 'input password'
 *         events: {
 *             input: function ( event ) {
 *                 debug.log(event.value);
 *             }
 *         }
 *     });
 */
function Input ( config ) {
    // sanitize
    config = config || {};

    if ( DEVELOP ) {
        if ( typeof config !== 'object' ) {
            throw new Error(__filename + ': wrong config type');
        }
        // init parameters checks
        if ( config.className && typeof config.className !== 'string' ) {
            throw new Error(__filename + ': wrong or empty config.className');
        }
    }

    /**
     * Text value of input.
     *
     * @type {string}
     */
    this.value = '';

    /**
     * Input type, now available only text and password.
     * Different logic with different types.
     * TYPE_TEXT - normal input.
     * TYPE_PASSWORD - hidden input, all chars replaced with '*', but real value is located in 'this.value'.
     *
     * @type {number}
     */
    this.type = this.TYPE_TEXT;

    // set default className if classList property empty or undefined
    config.className = 'input ' + (config.className || '');

    // parent constructor call
    Component.call(this, config);

    // insert text line
    this.$line = this.$body.appendChild(document.createElement('div'));
    // correct class
    this.$line.className = 'line';

    // element to show current cursor position
    this.$caret = this.$line.appendChild(document.createElement('div'));
    // correct class
    this.$caret.className = 'caret';

    // hint element with placeholder text
    this.$placeholder = this.$line.appendChild(document.createElement('div'));
    // correct class
    this.$placeholder.className = 'placeholder';

    // setup caret index
    this.$caret.index = 0;

    // component setup
    this.init(config);
}


// inheritance
Input.prototype = Object.create(Component.prototype);
Input.prototype.constructor = Input;

// input types
Input.prototype.TYPE_TEXT = 0;
Input.prototype.TYPE_PASSWORD = 1;


/**
 * List of all default event callbacks.
 *
 * @type {Object.<string, function>}
 */
Input.prototype.defaultEvents = {
    /**
     * Default method to handle keyboard keypress events.
     *
     * @param {Event} event generated event
     */
    keypress: function () {
        // this.addChar(String.fromCharCode(event.keyCode), this.$caret.index);
    },

    /**
     * Default method to handle keyboard keydown events.
     *
     * @param {Event} event generated event
     */
    keydown: function ( event ) {
        switch ( event.code ) {
            case keys['delete']:
                this.removeChar(this.$caret.index);
                break;

                // case keys.back:
                //     if ( !PLATFORM === 'TIZEN' ) {
                //         this.removeChar(this.$caret.index - 1);
                //     }
                //     break;

            case keys.left:
                this.setCaretPosition(this.$caret.index - 1);
                break;

            case keys.right:
                this.setCaretPosition(this.$caret.index + 1);
                break;

            case keys.end:
                // case keys.down:
                this.setCaretPosition(this.value.length);
                break;

            case keys.home:
                // case keys.up:
                this.setCaretPosition(0);
                break;

            default:
                break;
        }
    }
};


/**
 * Init or re-init of the component inner structures and HTML.
 *
 * @param {Object} config init parameters (subset of constructor config params)
 */
Input.prototype.init = function ( config ) {
    if ( DEVELOP ) {
        if ( config.type && Number(config.type ) !== config.type) { throw new Error(__filename + ': config.type must be a number'); }
        if ( config.type && config.type !== this.TYPE_TEXT && config.type !== this.TYPE_PASSWORD ) { throw new Error(__filename + ': config.type must be one of the TYPE_* constant'); }
        if ( config.value && typeof config.value !== 'string' ) { throw new Error(__filename + ': config.value must be a string'); }
        if ( config.placeholder && typeof config.placeholder !== 'string' ) { throw new Error(__filename + ': config.placeholder must be a string'); }
        if ( config.direction && typeof config.direction !== 'string' ) { throw new Error(__filename + ': config.direction must be a string'); }
        if ( config.direction && config.direction !== 'ltr' && config.direction !== 'rtl' ) { throw new Error(__filename + ': config.direction wrong value'); }
    }

    // type passed
    if ( typeof config.type !== 'undefined' ) {
        // apply
        this.type = config.type;
    }

    // default value passed
    if ( config.value ) {
        // apply
        this.setValue(config.value);
    }

    // hint
    if ( config.placeholder ) {
        // apply
        this.$placeholder.innerText = config.placeholder;
    }

    // char direction
    this.$line.dir = config.direction || 'ltr';
};


/**
 * Add given char to given position.
 * Also moving caret in every action.
 * Do nothing if position is < 0, or if index more or equals to length add char to the end.
 *
 * @param {string} char symbol to add
 * @param {number} [index=this.value.length] given position
 *
 * @fires module:stb/ui/input~Input#input
 */
Input.prototype.addChar = function ( char, index ) {
    var $char = document.createElement('div');

    index = (index === undefined) ? this.$caret.index : index;

    if ( DEVELOP ) {
        if ( index < 0 ) { throw new Error(__filename + ': index must be more than 0 or equal to 0'); }
        if ( typeof char !== 'string' ) { throw new Error(__filename + ': char must be a string'); }
        if ( char.length !== 1 ) { throw new Error(__filename + ': char must be a string with length = 1'); }
    }

    // remove hint
    if ( this.value.length === 0 ) {
        this.$line.removeChild(this.$placeholder);
    }

    // settings class name for span which presents one symbol in virtual input
    $char.className = 'char';

    // move caret
    // ++this.$caret.index;

    if ( this.type === this.TYPE_PASSWORD ) {
        $char.innerText = '*';
    } else if ( char === ' ' ) {
        $char.innerHTML = '&nbsp;';
    } else {
        $char.innerText = char;
    }

    console.log('add char: ' + JSON.stringify($char.innerText) + ' to index ' + index);
    console.log('caret index before ' + this.$caret.index);
    console.log('this.value.length ' + this.value.length);
    if ( index >= this.value.length ) {
        console.log('add char to the end, move caret to the end');
        this.$line.appendChild($char);
        // this.$line.appendChild(this.$caret);
    } else {
        console.log('move caret before index, append span before caret');
        this.$line.insertBefore(this.$caret, this.$line.children[index]);
        this.$line.insertBefore($char, this.$caret);
    }

    // insert char into value
    this.value = this.value.substring(0, index) + char + this.value.substring(index, this.value.length);

    this.setCaretPosition(this.$caret.index + 1);

    // there are some listeners
    if ( this.events['input'] ) {
        // notify listeners
        this.emit('input', {value: this.value});
    }
};


/**
 * Remove char from given position.
 * Do nothing if index is out of the range (0, length).
 *
 * @param {number} [index=this.$caret.index - 1] index given position
 *
 * @fires module:stb/ui/input~Input#input
 */
Input.prototype.removeChar = function ( index ) {
    index = (index === undefined) ? this.$caret.index - 1 : index;
    // non-empty string
    if ( this.value.length > 0 ) {
        if ( DEVELOP ) {
            if ( index < 0 ) { throw new Error(__filename + ': index must be a positive value'); }
            if ( index > this.value.length ) { throw new Error(__filename + ': index must be a less than or equal to total length'); }
        }

        console.log('caret index before ' + this.$caret.index);
        console.log('remove from index ' + index);
        console.log('this.value.length ' + this.value.length);
        if ( this.$caret.index === index && index < this.value.length ) {
            console.log('remove char after caret');
            this.$line.removeChild(this.$line.children[index + 1]);
        } else if ( this.$caret.index > index ) {
            console.log('remove char before caret');
            // --this.$caret.index;
            this.$line.removeChild(this.$line.children[index]);
        }

        this.setCaretPosition(this.$caret.index - 1);

        // cut one char from the value
        this.value = this.value.substring(0, index) + this.value.substring(index + 1, this.value.length);

        // there are some listeners and value was changed
        if ( this.events['input'] ) {
            // notify listeners
            this.emit('input', {value: this.value});
        }
    }

    // only hint
    if ( this.value.length === 0 ) {
        this.$line.appendChild(this.$placeholder);
    }
};


/**
 * Move caret to the given position.
 * Do nothing if index is out of the range (0, this.value.length).
 *
 * @param {number} index given position
 */
Input.prototype.setCaretPosition = function ( index ) {
    var caretOffset;

    if ( !this.offsetLeft ) {
        this.overflowRight = this.$line.offsetLeft + this.$line.offsetWidth;
        this.overflowLeft = this.$line.offsetLeft;
    }

    // check boundaries and current position
    if ( index >= 0 && index <= this.value.length && this.$caret.index !== index ) {
        // remove a caret
        this.$line.removeChild(this.$caret);

        if ( index === this.value.length ) {
            // add a caret to the end of the input line
            this.$line.appendChild(this.$caret);
        } else {
            // add a caret to any other place
            this.$line.insertBefore(this.$caret, this.$line.children[index]);
        }

        // change an input scroll if a caret has been moved to the right
        if ( index > this.$caret.index ) {
            caretOffset = this.$caret.offsetLeft + this.$caret.offsetWidth + this.$caret.previousSibling.offsetWidth;

            if ( caretOffset >= this.overflowRight ) {
                if ( index === this.value.length ) {
                    this.$line.scrollLeft = this.$caret.offsetLeft + this.$caret.offsetWidth - this.overflowRight;
                } else {
                    this.$line.scrollLeft += this.$caret.previousSibling.offsetWidth;
                }
            }
            // change an input scroll if a caret has been moved to the left
        } else {
            caretOffset = this.$caret.getBoundingClientRect().left;

            if ( caretOffset <= this.overflowLeft ) {
                if ( this.$caret.previousSibling ) {
                    this.$line.scrollLeft -= this.$caret.previousSibling.offsetWidth;
                } else {
                    this.$line.scrollLeft = 0;
                }
            }
        }

        this.$caret.index = index;
    }

    console.log('this.$line.scrollLeft ' + this.$line.scrollLeft);
    console.log('$caret.index ' + this.$caret.index);
};

/**
 * Move caret to the given position.
 * Do nothing if index is out of the range (0, this.value.length).
 *
 * @param {number} index given position
 *
 * @return {number} caret index
 */
Input.prototype.getCaretPosition = function () {
    // check boundaries and current position
    return this.$caret.index;
};


/**
 * Setting new text value of the input field.
 *
 * @param {string} value given string value
 */
Input.prototype.setValue = function ( value ) {
    var oldLength = this.value.length,
        newLength = value.length,
        index     = 0,
        $char, diff;

    if ( DEVELOP ) {
        if ( typeof value !== 'string' ) { throw new Error(__filename + ': value must be a string'); }
    }

    // return if no changes
    // if ( value === this.value ) {
    //     return;
    // }

    // non-empty string
    if ( newLength > 0 ) {
        // no hint
        if ( this.$placeholder.parentNode === this.$line ) {
            this.$line.removeChild(this.$placeholder);
        }

        // no cursor
        this.$line.removeChild(this.$caret);

        // value length has changed
        if ( newLength !== oldLength ) {
            diff = newLength - oldLength;

            // need to correct char divs amount
            if ( diff > 0 ) {
                // add missing chars
                for ( index = 0; index < diff; index++ ) {
                    $char = this.$line.appendChild(document.createElement('div'));
                    $char.className = 'char';
                }
            } else {
                // remove unnecessary chars
                for ( index = 0; index > diff; index-- ) {
                    this.$line.removeChild(this.$line.lastChild);
                }
            }
        }

        // apply value
        for ( index = 0; index < newLength; index++ ) {
            $char = this.$line.children[index];
            if ( this.type === this.TYPE_PASSWORD ) {
                $char.innerHTML = '*';
            } else if ( value[index] === ' ' ) {
                $char.innerHTML = '&nbsp;';
            } else {
                $char.innerText = value[index];
            }
        }

        this.value = value;
        this.$caret.index = newLength;
        this.$line.appendChild(this.$caret);
    } else {
        // empty string
        this.value = '';
        this.$line.innerText = '';
        this.$line.appendChild(this.$caret);
        this.$line.appendChild(this.$placeholder);
        this.$caret.index = 0;
    }

    // there are some listeners
    if ( this.events['input'] ) {
        // notify listeners
        this.emit('input', {value: this.value});
    }
};


Input.prototype.changeView = function ( className, action ) {
    console.log('changeView ' + action + ': ' + className);
    if ( action === 'add' ) {
        this.$node.classList.add(className);
    } else if ( action === 'remove' ) {
        this.$node.classList.remove(className);
    }

    this.overflowRight = this.$line.offsetLeft + this.$line.offsetWidth;
    console.log('this.overflowRight ' + this.overflowRight);
    this.overflowLeft = this.$line.offsetLeft;
    console.log('this.overflowLeft ' + this.overflowLeft);
    this.$line.scrollLeft = 30000;
};


// public
module.exports = Input;
